import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { X, Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const CartCtx = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("nut_cart");
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem("nut_cart", JSON.stringify(items));
  }, [items]);

  const closeCart = useCallback(() => setOpen(false), []);
  const openCart = useCallback(() => setOpen(true), []);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeCart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, closeCart]);

  // Prevent body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const addItem = useCallback(
    (p, qty = 1) => {
      setItems((prev) => {
        const found = prev.find((i) => i.id === p.id);
        if (found)
          return prev.map((i) => (i.id === p.id ? { ...i, qty: i.qty + qty } : i));
        return [
          ...prev,
          { id: p.id, name: p.name, price: p.price, image: p.image, weight: p.weight, qty },
        ];
      });
      toast({ title: "Added to cart", description: `${p.name} – ${p.weight}` });
    },
    [toast]
  );

  const updateQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qty: Math.max(0, qty) } : i))
        .filter((i) => i.qty > 0)
    );
  }, []);

  const removeItem = useCallback(
    (id) => setItems((prev) => prev.filter((i) => i.id !== id)),
    []
  );

  const count = items.reduce((a, c) => a + c.qty, 0);
  const total = items.reduce((a, c) => a + c.qty * c.price, 0);

  return (
    <CartCtx.Provider
      value={{ items, addItem, updateQty, removeItem, count, total, openCart, closeCart }}
    >
      {children}

      {/* Overlay */}
      <div
        onClick={closeCart}
        aria-hidden={!open}
        className="fixed inset-0 bg-black/40 transition-opacity duration-300"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          zIndex: 60,
        }}
      />

      {/* Drawer */}
      <aside
        role="dialog"
        aria-label="Shopping cart"
        aria-hidden={!open}
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col transition-transform duration-300"
        style={{
          transform: open ? "translateX(0)" : "translateX(100%)",
          zIndex: 70,
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b">
          <div className="flex items-center gap-2 font-medium">
            <ShoppingBag size={18} /> Your basket
          </div>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Close cart"
            className="text-stone-600 hover:text-stone-900 p-2 -mr-2"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 && (
            <div className="text-center text-stone-500 py-16">
              <ShoppingBag size={32} className="mx-auto text-stone-300" />
              <p className="mt-3">No nuts in your basket yet.</p>
            </div>
          )}
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 pb-4 border-b last:border-b-0">
              <img src={i.image} alt={i.name} className="w-20 h-20 object-cover bg-stone-50" />
              <div className="flex-1">
                <div className="text-sm font-medium text-stone-800">{i.name}</div>
                <div className="text-xs text-stone-500">{i.weight}</div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateQty(i.id, i.qty - 1)}
                    className="w-7 h-7 border flex items-center justify-center"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="text-sm w-6 text-center">{i.qty}</span>
                  <button
                    type="button"
                    onClick={() => updateQty(i.id, i.qty + 1)}
                    className="w-7 h-7 border flex items-center justify-center"
                    aria-label="Increase quantity"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(i.id)}
                    className="ml-auto text-stone-400 hover:text-[#c8232c]"
                    aria-label="Remove item"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="text-sm font-semibold">€{(i.price * i.qty).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="border-t p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-stone-600">Subtotal</span>
            <span className="text-lg font-semibold">€{total.toFixed(2)}</span>
          </div>
          <button
            type="button"
            disabled={items.length === 0}
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            Checkout
          </button>
          <div className="text-xs text-center text-stone-500 mt-3">
            Free shipping in Europe above €49
          </div>
        </div>
      </aside>
    </CartCtx.Provider>
  );
};

export const useCart = () => useContext(CartCtx);
